import React, { useRef, useState, useEffect } from "react";
import Hls from "hls.js";
import { Play, Pause, Volume2, VolumeX, Maximize2, Star, Share2, Trash2, Gauge, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Video player used inside modals + share pages. Supports:
// - Standard controls (play/pause/scrub/volume/fullscreen)
// - Playback speed presets (0.25× 0.5× 1× 2× 4×)
// - Star / Share / Delete actions in-line (hidden if `readOnly`)
// - A "TRADEGREEN" watermark in the corner on shared clips
// - Auto-upgrade to Bunny Stream HLS (fast global CDN) when the clip is
//   ready on Bunny. Falls back to the backend stream URL otherwise.
export default function VideoPlayer({
  clip,
  streamUrl,           // fallback backend stream URL (used until Bunny ready)
  readOnly = false,    // hide star/share/delete when viewing a shared clip
  showWatermark = false,
  onClose,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [dur, setDur] = useState(clip?.duration_seconds || 0);
  const [speed, setSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffering, setBuffering] = useState(true);
  // Actual URL we hand to the <video> element. Starts as the fallback URL and
  // upgrades to a signed Bunny HLS URL if the clip is ready on the CDN.
  const [activeUrl, setActiveUrl] = useState(streamUrl);
  const [isHls, setIsHls] = useState(false);
  // Encoding / error state pulled from /playback response for direct-Bunny clips.
  // encodingState: "ready" | "encoding" | "encoding_error" | "upload_failed" | null
  const [encodingState, setEncodingState] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const qc = useQueryClient();

  // Track browser fullscreen state so we can switch the video sizing between
  // "cap at 70vh" (embed) and "fill the screen" (fullscreen). Without this the
  // video stays capped in fullscreen mode, leaving giant black borders.
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Ask the backend for the best playback URL for this clip. If Bunny has
  // finished encoding, we get back a signed HLS URL served from Bunny's
  // global CDN (sub-2s first frame anywhere). Otherwise we stick with the
  // passed-in backend stream URL — OR, for direct-Bunny clips that aren't
  // ready yet, we show an "encoding in progress" UI with a manual retry
  // button (safety net for when Bunny's webhook doesn't fire).
  //
  // Skipped for share pages (readOnly=true) which don't have a clip.id we
  // can query, and for legacy clips that never got a Bunny upload.
  useEffect(() => {
    let cancelled = false;
    if (!clip?.id || readOnly) {
      setActiveUrl(streamUrl);
      setIsHls(false);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/video/clips/${clip.id}/playback`);
        if (cancelled) return;
        const d = r.data || {};
        if (d.poster) setPosterUrl(d.poster);
        if (d.source === "bunny" && d.url && d.hls && d.ready) {
          setEncodingState("ready");
          setActiveUrl(d.url);
          setIsHls(true);
        } else if (d.bunny_error) {
          // Bunny reported an error state (5 = encoding error, 6 = upload failed).
          // Stop trying to play and let the user see a clear error.
          setEncodingState(d.bunny_error);
          setActiveUrl(null);
          setIsHls(false);
        } else if (!d.ready && !d.url) {
          // Direct-Bunny clip still encoding; no fallback URL available.
          setEncodingState("encoding");
          setActiveUrl(null);
          setIsHls(false);
        } else {
          // Legacy path: fall back to backend stream URL while Bunny finishes.
          setEncodingState(d.encoding ? "encoding" : "ready");
          setActiveUrl(d.url || streamUrl);
          setIsHls(false);
        }
      } catch (_e) {
        // Fallback silently to backend stream (works for legacy clips only).
        if (!cancelled) {
          setActiveUrl(streamUrl);
          setIsHls(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [clip?.id, streamUrl, readOnly]);

  const refreshBunnyStatus = async () => {
    if (!clip?.id) return;
    setRefreshingStatus(true);
    try {
      const r = await api.post(`/video/clips/${clip.id}/bunny-refresh`);
      const { bunny_ready, bunny_error } = r.data || {};
      if (bunny_ready) {
        // Re-fetch the playback URL to get the signed CDN link.
        toast.success("Ready! Loading video…");
        const p = await api.get(`/video/clips/${clip.id}/playback`);
        const d = p.data || {};
        if (d.url && d.hls) {
          setEncodingState("ready");
          setActiveUrl(d.url);
          setIsHls(true);
        }
      } else if (bunny_error) {
        setEncodingState(bunny_error);
        toast.error(bunny_error === "upload_failed" ? "Upload failed on Bunny CDN. Re-upload this clip." : "Encoding failed. Re-upload this clip.");
      } else {
        toast("Still encoding — Bunny is a bit slow today. Try again in ~30 seconds.", { icon: "⏳" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach Bunny CDN.");
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Attach the source to the <video> element. For HLS we use hls.js on
  // Chrome/Firefox/Edge and native playback on Safari (which supports HLS
  // out of the box). For plain MP4 we just set the `src` attribute.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !activeUrl) return;
    // Tear down any previous hls.js instance before switching sources.
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (_e) { /* ignore */ }
      hlsRef.current = null;
    }
    if (isHls) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          // Start at a mid-high quality (720p, index 3 in our 240p→1080p ladder)
          // so the very first frame is readable — trade-review videos have
          // dense text (chart symbols, order-book cells) that gets unusable at
          // 240p/360p. ABR will still downgrade if the user's bandwidth is
          // truly poor, but the default should look sharp immediately.
          startLevel: 3,
          // Cap streamed quality to what the player element can actually show.
          // Prevents wasting bandwidth on 1080p when the modal is 720p tall.
          capLevelToPlayerSize: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          // Optimistic bandwidth estimate on cold start (10 Mbps) so ABR
          // picks a high quality immediately instead of ramping up over a
          // few segments. If it's wrong, hls.js re-estimates within seconds.
          abrEwmaDefaultEstimate: 10_000_000,
          lowLatencyMode: false,
        });
        // If HLS fails fatally (network error, manifest parse error, etc.)
        // fall back to the backend MP4 stream URL so the video still plays.
        // Previously we just hung on a spinner — user had no way to recover.
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data?.fatal) return;
          const badUrl = streamUrl;
          if (!badUrl) return;
          try { hls.destroy(); } catch (_e) { /* ignore */ }
          hlsRef.current = null;
          setIsHls(false);
          setActiveUrl(badUrl);
          v.src = badUrl;
          v.load();
        });
        hls.loadSource(activeUrl);
        hls.attachMedia(v);
        hlsRef.current = hls;
      } else if (v.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari — native HLS
        v.src = activeUrl;
      } else {
        // No HLS support — should be rare (IE/legacy). Fall back to backend URL.
        v.src = streamUrl;
      }
    } else {
      // Plain MP4 from backend
      v.src = activeUrl;
    }

    // Stall watchdog — HLS.js sometimes silently fails on the very first
    // segment fetch (flaky ISP DNS, CDN edge warming up, referer race, etc.).
    // Multi-stage fallback:
    //   • 3s: if MANIFEST_LOADED hasn't fired, Bunny is unreachable → backend
    //   • 6s: if readyState < HAVE_METADATA (1), no segments arriving → backend
    //   • 10s: if readyState < HAVE_CURRENT_DATA (2), decode stuck → backend
    // This keeps users from staring at a black spinner for minutes.
    let watchdog = null;
    let manifestLoaded = false;
    if (isHls && streamUrl) {
      if (hlsRef.current) {
        hlsRef.current.on(Hls.Events.MANIFEST_LOADED, () => { manifestLoaded = true; });
      }
      const started = Date.now();
      const doFallback = () => {
        if (watchdog) { clearInterval(watchdog); watchdog = null; }
        if (hlsRef.current) {
          try { hlsRef.current.destroy(); } catch (_e) { /* ignore */ }
          hlsRef.current = null;
        }
        setIsHls(false);
        setActiveUrl(streamUrl);
        v.src = streamUrl;
        v.load();
      };
      watchdog = setInterval(() => {
        if (v.readyState >= 2) {
          clearInterval(watchdog);
          watchdog = null;
          return;
        }
        const elapsed = Date.now() - started;
        // Only fall back on genuine stalls. Progressive thresholds so we
        // don't kill a slow-but-working load.
        if (elapsed > 3000 && !manifestLoaded) { doFallback(); return; }
        if (elapsed > 6000 && v.readyState < 1) { doFallback(); return; }
        if (elapsed > 10000 && v.readyState < 2) { doFallback(); return; }
      }, 500);
    }

    return () => {
      if (watchdog) clearInterval(watchdog);
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_e) { /* ignore */ }
        hlsRef.current = null;
      }
    };
  }, [activeUrl, isHls, streamUrl]);

  // Poster thumbnail — cached in localStorage by clip id. Loaded once (if
  // available) so the video element shows the first-frame image instantly
  // instead of a black screen while it buffers.
  const posterKey = `tg_poster_${clip?.id || ""}`;
  const [posterUrl, setPosterUrl] = useState(() => {
    try { return clip?.id ? localStorage.getItem(posterKey) || undefined : undefined; }
    catch { return undefined; }
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrent(v.currentTime);
    const onLoaded = () => setDur(v.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    // Buffering signals — `waiting` fires when the browser stalls, `canplay`
    // fires the moment there's enough data to render frames.
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => {
      setBuffering(false);
      // Grab the first-frame thumbnail once — cached in localStorage so every
      // subsequent open of this clip shows the frame instead of a black screen.
      if (!posterUrl && clip?.id) {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = 320;
          canvas.height = Math.round((v.videoHeight || 180) * (320 / (v.videoWidth || 320)));
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/jpeg", 0.55);  // ~5-15 KB per thumbnail
            if (dataUrl && dataUrl.length < 200_000) {
              localStorage.setItem(posterKey, dataUrl);
              setPosterUrl(dataUrl);
            }
          }
        } catch (_e) { /* canvas-tainted or CORS — skip */ }
      }
    };
    const onPlaying = () => setBuffering(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("playing", onPlaying);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("playing", onPlaying);
    };
  }, [activeUrl, posterUrl, clip?.id, posterKey]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
  }, [speed]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    // If the clip finished, `paused` is true but `currentTime === duration` so
    // calling play() alone does nothing (the video appears frozen on the last
    // frame). Reset to the start so the user can replay without reloading.
    if (v.ended || (v.duration && v.currentTime >= v.duration - 0.05)) {
      try { v.currentTime = 0; } catch (_e) { /* ignore */ }
    }
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay may reject — ignore */ });
    } else {
      v.pause();
    }
  };

  // Scrub to `pct` (0..1) along the timeline.
  //  - `fastSeek` when available (Chrome, Safari, Edge): jumps to the nearest
  //     keyframe instantly rather than decoding every intermediate frame.
  //     Massive UX win while dragging the timeline.
  //  - Falls back to `currentTime =` on Firefox / older engines.
  const scrub = (pct) => {
    const v = videoRef.current;
    if (!v || !dur) return;
    const t = Math.max(0, Math.min(dur, dur * pct));
    if (typeof v.fastSeek === "function") {
      try { v.fastSeek(t); return; } catch (_e) { /* fall through */ }
    }
    v.currentTime = t;
  };

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const toggleStar = async () => {
    try {
      await api.patch(`/video/clips/${clip.id}`, { starred: !clip.starred });
      toast.success(!clip.starred ? "Clip starred — kept forever" : "Star removed");
      qc.invalidateQueries();
    } catch (e) { toast.error("Failed"); }
  };

  const share = async () => {
    try {
      let token = clip.share_token;
      if (!token) {
        const r = await api.post(`/video/clips/${clip.id}/share`);
        token = r.data.share_token;
        qc.invalidateQueries();
      }
      const url = `${window.location.origin}/clip/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch (e) { toast.error("Share failed"); }
  };

  const deleteClip = async () => {
    if (!window.confirm("Delete this clip permanently?")) return;
    try {
      await api.delete(`/video/clips/${clip.id}`);
      toast.success("Clip deleted");
      qc.invalidateQueries();
      onClose?.();
    } catch (e) { toast.error("Delete failed"); }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const rem = Math.floor(s % 60);
    return `${m}:${String(rem).padStart(2, "0")}`;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{
        background: "black",
        borderRadius: isFullscreen ? 0 : "12px",
        overflow: "hidden",
        // In fullscreen the container itself becomes 100vw × 100vh; flex-center
        // the video so it always fills the available height (no black borders
        // beyond what aspect ratio requires).
        ...(isFullscreen ? { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", width: "100vw" } : {}),
      }}
      data-testid="video-player"
    >
      <video
        ref={videoRef}
        // src is set programmatically in the effect above — either via hls.js
        // (attachMedia) for HLS URLs or via v.src = ... for plain MP4.
        preload="auto"
        playsInline
        // Poster = cached first-frame thumbnail. Shown instantly on 2nd+ open
        // of the same clip so the user never stares at a black screen while
        // the video bytes stream in.
        poster={posterUrl}
        className={isFullscreen ? "block" : "w-full block"}
        style={
          isFullscreen
            ? { background: "black", height: "100vh", width: "100vw", objectFit: "contain" }
            : { background: "black", maxHeight: "70vh" }
        }
        onClick={toggle}
        controls={false}
      />

      {/* Encoding / error overlay — for direct-Bunny clips that aren't ready
          yet, or clips where Bunny failed. Takes priority over the normal
          buffering overlay so users see a clear "still encoding" state
          instead of an infinite spinner. */}
      {encodingState && encodingState !== "ready" && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
          data-testid="video-encoding-overlay"
          style={{ background: "rgba(0,0,0,0.85)", zIndex: 10 }}
        >
          {clip?.symbol && (
            <div className="mb-4">
              <div className="text-2xl font-semibold tracking-tight" style={{ color: "#FAFAFA", fontFamily: "var(--font-heading)" }}>
                {clip.symbol}
              </div>
              <div className="text-xs mt-1 font-mono-num" style={{ color: "rgba(250,250,250,0.5)" }}>
                {clip.date}
              </div>
            </div>
          )}
          {encodingState === "encoding" && (
            <>
              <div
                className="animate-spin rounded-full h-8 w-8 mb-4"
                style={{ borderWidth: 2, borderStyle: "solid", borderColor: "rgba(255,255,255,0.15)", borderTopColor: "#A855F7" }}
              />
              <div className="text-sm font-medium mb-1" style={{ color: "#FAFAFA" }}>Bunny is encoding your clip</div>
              <div className="text-xs mb-4" style={{ color: "rgba(250,250,250,0.5)" }}>
                Usually takes 30–90 seconds. If it&apos;s been longer, tap below to refresh.
              </div>
              <button
                onClick={refreshBunnyStatus}
                disabled={refreshingStatus}
                data-testid="video-refresh-status"
                className="px-4 py-2 text-xs tracking-wider font-medium transition-colors disabled:opacity-50"
                style={{ background: "var(--accent)", color: "#FAFAFA", borderRadius: 8 }}
              >
                {refreshingStatus ? "CHECKING…" : "RETRY STATUS CHECK"}
              </button>
            </>
          )}
          {(encodingState === "encoding_error" || encodingState === "upload_failed") && (
            <>
              <div className="text-3xl mb-3">⚠️</div>
              <div className="text-sm font-medium mb-1" style={{ color: "#F43F5E" }}>
                {encodingState === "upload_failed" ? "Upload failed on the CDN" : "Encoding failed"}
              </div>
              <div className="text-xs mb-4 max-w-md" style={{ color: "rgba(250,250,250,0.6)" }}>
                Bunny couldn&apos;t process this clip. This clip won&apos;t play — delete it and re-upload from your OBS recording.
              </div>
              <button
                onClick={refreshBunnyStatus}
                disabled={refreshingStatus}
                data-testid="video-refresh-status"
                className="px-4 py-2 text-xs tracking-wider font-medium transition-colors disabled:opacity-50"
                style={{ background: "var(--surface)", color: "var(--fg-2)", border: "1px solid var(--border)", borderRadius: 8 }}
              >
                {refreshingStatus ? "CHECKING…" : "TRY AGAIN"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Buffering overlay — spinner + trade context. Shows symbol / P&L / date
          alongside the spinner so the loading state has actual info, not just
          a black frame. Auto-hides the moment the video can play. */}
      {buffering && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6"
          data-testid="video-buffering"
          style={{ background: posterUrl ? "rgba(0,0,0,0.30)" : "rgba(0,0,0,0.55)", zIndex: 5 }}
        >
          {clip?.symbol && (
            <div className="text-center mb-3">
              <div className="text-3xl font-semibold tracking-tight" style={{ color: "#FAFAFA", fontFamily: "var(--font-heading)" }}>
                {clip.symbol}
              </div>
              <div className="text-sm mt-1 font-mono-num" style={{ color: (clip.pnl || 0) >= 0 ? "#3EE07C" : "#F43F5E" }}>
                {(clip.pnl || 0) >= 0 ? "+" : ""}${(clip.pnl || 0).toFixed(2)}
                <span style={{ color: "rgba(250,250,250,0.5)", marginLeft: 8 }}>· {clip.date}</span>
              </div>
            </div>
          )}
          <div
            className="animate-spin rounded-full h-6 w-6"
            style={{ borderWidth: 2, borderStyle: "solid", borderColor: "rgba(255,255,255,0.15)", borderTopColor: "#A855F7" }}
          />
        </div>
      )}

      {/* Watermark overlay for shared clips */}
      {showWatermark && (
        <div
          className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono-num tracking-widest pointer-events-none"
          style={{ background: "rgba(0,0,0,0.5)", color: "white", borderRadius: "4px", backdropFilter: "blur(4px)" }}
        >
          <span style={{ color: "#3EE07C" }}>●</span> TRADEGREEN
        </div>
      )}

      {/* Custom controls overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.85), transparent)" }}>
        {/* Timeline — click anywhere to seek. Big invisible click target (h-4)
            wraps a slim visible bar (h-2) with an on-hover thumbstick so users
            can tell it's interactive without a floating cursor. Uses onMouseDown
            for immediate response + drag-to-scrub. */}
        <div
          className="relative w-full h-4 flex items-center cursor-pointer group"
          onMouseDown={(e) => {
            const bar = e.currentTarget;
            const rect = bar.getBoundingClientRect();
            const doScrub = (clientX) => scrub((clientX - rect.left) / rect.width);
            doScrub(e.clientX);
            // Drag-to-scrub — track mousemove until release. rAF-throttled so
            // we call `scrub()` at most once per animation frame; without this,
            // dragging fast pumps 200+ seeks/sec and the video stutters.
            let rafId = 0;
            let latestX = e.clientX;
            const onMove = (ev) => {
              latestX = ev.clientX;
              if (rafId) return;
              rafId = requestAnimationFrame(() => { rafId = 0; doScrub(latestX); });
            };
            const onUp = () => {
              if (rafId) cancelAnimationFrame(rafId);
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
          data-testid="video-timeline"
        >
          <div className="relative w-full h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }}>
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${dur ? (current / dur) * 100 : 0}%`, background: "var(--accent)" }}
            />
            {/* Thumbstick — always visible for clear affordance */}
            <div
              className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none transition-transform group-hover:scale-125"
              style={{
                left: `${dur ? (current / dur) * 100 : 0}%`,
                transform: "translate(-50%, -50%)",
                width: "12px",
                height: "12px",
                background: "white",
                boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
              }}
            />
          </div>
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-3 mt-2 text-white">
          <button onClick={toggle} data-testid="play-pause-btn">
            {playing ? <Pause className="h-5 w-5" fill="white" /> : <Play className="h-5 w-5" fill="white" />}
          </button>
          <div className="text-xs font-mono-num">{fmt(current)} / {fmt(dur)}</div>
          <div className="flex-1" />

          {/* Speed */}
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            data-testid="speed-select"
            className="text-xs font-mono-num bg-transparent border rounded px-1.5 py-0.5"
            style={{ borderColor: "rgba(255,255,255,0.3)", color: "white" }}
          >
            {[0.25, 0.5, 1, 1.5, 2, 4].map(s => (
              <option key={s} value={s} style={{ background: "#111" }}>{s}×</option>
            ))}
          </select>

          <button onClick={() => setMuted(m => { videoRef.current.muted = !m; return !m; })} data-testid="mute-btn">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>

          {!readOnly && (
            <>
              <button onClick={toggleStar} title={clip.starred ? "Unstar" : "Star (keep forever)"} data-testid="star-btn">
                <Star className="h-4 w-4" fill={clip.starred ? "#fbbf24" : "none"} stroke={clip.starred ? "#fbbf24" : "white"} />
              </button>
              <button onClick={share} title="Copy share link" data-testid="share-btn">
                <Share2 className="h-4 w-4" />
              </button>
              <button onClick={deleteClip} title="Delete" data-testid="delete-btn">
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
          <button onClick={goFullscreen} data-testid="fullscreen-btn">
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
